import { useMutation, useMutationState, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  getGetMarketingGoogleRankingProfileQueryKey,
  getMarketingGoogleRankingProfile,
  postMarketingGoogleRankingProfileRefresh,
  postMarketingGoogleRankingProfileSuggestionsApproveAll,
  postMarketingGoogleRankingProfileSuggestionsIdApprove,
  postMarketingGoogleRankingProfileSuggestionsIdSkip,
} from '@/api/generated/google-ranking/google-ranking';
import type {
  GbpProfileSuggestion,
  GetMarketingGoogleRankingProfile200,
  GetMarketingGoogleRankingProfile200Data,
} from '@/api/generated/schemas';
import { useOrg } from '@/providers';
import {
  asQueued,
  decisionIds,
  PROFILE_POLL_MS,
  profileRefetchInterval,
  restoreSuggestionRows,
} from './google-ranking-profile-optimistic';

export type GoogleRankingProfile = GetMarketingGoogleRankingProfile200Data;

const DECISION_MUTATION_KEY = ['google-ranking-profile', 'decision'] as const;

/**
 * Keyed by the real org id from `useOrg()` (never the `$orgId` route param, which is the salon shortCode), so
 * an agency user switching salons never sees another salon's fixes. The Google Ranking layout (availability
 * gate + tab count) and the Profile fixes tab share this one cache entry.
 */
export const googleRankingProfileQueryKey = (orgId: string) =>
  [...getGetMarketingGoogleRankingProfileQueryKey(), orgId] as const;

/**
 * The profile audit (database only — no Google call). Re-read on a schedule while something is in flight
 * (`profileRefetchInterval`: queued approvals, the first read), and every few seconds while `pollWhile`
 * (a "Check again" running).
 */
/** Key, read and freshness of the profile query, shared by every reader of its cache entry. */
export const googleRankingProfileQueryOptions = (orgId: string) => ({
  queryKey: googleRankingProfileQueryKey(orgId),
  queryFn: ({ signal }: { signal: AbortSignal }) => getMarketingGoogleRankingProfile(signal),
  staleTime: 30_000,
});

export function useGoogleRankingProfile({ pollWhile = false }: { pollWhile?: boolean } = {}) {
  const { orgId } = useOrg();
  return useQuery({
    ...googleRankingProfileQueryOptions(orgId),
    select: (res) => res.data,
    refetchInterval: (query) => (pollWhile ? PROFILE_POLL_MS.inFlight : profileRefetchInterval(query.state.data?.data, Date.now())),
  });
}

type ProfileCache = GetMarketingGoogleRankingProfile200;

function patchSuggestions(
  queryClient: QueryClient,
  key: readonly unknown[],
  patch: (suggestions: GbpProfileSuggestion[]) => GbpProfileSuggestion[]
) {
  queryClient.setQueryData<ProfileCache>(key, (old) =>
    old ? { ...old, data: { ...old.data, suggestions: patch(old.data.suggestions) } } : old
  );
}

/**
 * Shared optimistic flow for approve / skip / approve-all: patch the cached list right away, on failure put
 * back only the rows THIS decision touched (other decisions still in flight keep their state), then refetch — only after the LAST of several quick decisions, so an earlier refetch
 * can't bring back a card the server hasn't handled yet.
 */
function useProfileDecision<TVars, TResult>(
  mutationFn: (vars: TVars) => Promise<TResult>,
  optimistic: (suggestions: GbpProfileSuggestion[], vars: TVars) => GbpProfileSuggestion[]
) {
  const { orgId } = useOrg();
  const queryClient = useQueryClient();
  const key = googleRankingProfileQueryKey(orgId);
  return useMutation({
    mutationKey: DECISION_MUTATION_KEY,
    mutationFn,
    onMutate: async (vars: TVars) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ProfileCache>(key);
      patchSuggestions(queryClient, key, (suggestions) => optimistic(suggestions, vars));
      return { previous };
    },
    onError: (_error, vars, context) => {
      const previous = context?.previous?.data.suggestions;
      if (!previous) return;
      const ids = new Set(decisionIds(vars));
      patchSuggestions(queryClient, key, (current) => restoreSuggestionRows(current, previous, ids));
    },
    onSettled: () => {
      if (queryClient.isMutating({ mutationKey: DECISION_MUTATION_KEY }) > 1) return;
      return queryClient.invalidateQueries({ queryKey: key });
    },
  });
}

export type ApproveSuggestionVars = { id: string; editedValue?: Record<string, unknown> };

/** Approve one fix (optionally the owner's edited value). 400 → `getApiErrorIssues`; 409 NOT_OPEN → already handled. */
export function useApproveProfileSuggestion() {
  return useProfileDecision(
    ({ id, editedValue }: ApproveSuggestionVars) =>
      postMarketingGoogleRankingProfileSuggestionsIdApprove(id, editedValue ? { editedValue } : {}),
    (suggestions, { id }) => {
      const now = new Date().toISOString();
      return suggestions.map((s) => (s.id === id ? asQueued(s, now) : s));
    }
  );
}

/** Skip a fix: Hayven never offers these exact values again, so the card leaves the list. */
export function useSkipProfileSuggestion() {
  return useProfileDecision(
    (id: string) => postMarketingGoogleRankingProfileSuggestionsIdSkip(id),
    (suggestions, id) => suggestions.filter((s) => s.id !== id)
  );
}

/** Approve every listed OPEN fix as Hayven proposed it. The response names the ones that could not go. */
export function useApproveAllProfileSuggestions() {
  return useProfileDecision(
    (ids: string[]) => postMarketingGoogleRankingProfileSuggestionsApproveAll({ ids }),
    (suggestions, ids) => {
      const now = new Date().toISOString();
      const chosen = new Set(ids);
      return suggestions.map((s) => (chosen.has(s.id) && s.status === 'OPEN' ? asQueued(s, now) : s));
    }
  );
}

/**
 * Ids of fixes with a decision on its way (approve / skip / approve-all), so their buttons stay disabled until
 * the server answers — a fast double-click can't send two requests before the optimistic flip lands.
 */
export function usePendingProfileDecisionIds(): ReadonlySet<string> {
  const pending = useMutationState({
    filters: { mutationKey: DECISION_MUTATION_KEY, status: 'pending' },
    select: (mutation) => mutation.state.variables,
  });
  const ids = new Set<string>(pending.flatMap(decisionIds));
  return ids;
}

/**
 * "Check again": queue a fresh read of the Google profile (1 per 10 min per salon; 429 REFRESH_THROTTLED
 * carries `details.nextAllowedAt`). The report updates when the job finishes — callers poll meanwhile.
 */
export function useRefreshGoogleProfile() {
  return useMutation({
    mutationFn: () => postMarketingGoogleRankingProfileRefresh(),
  });
}
